import { useMemo } from "react";

import type { KbNode } from "@/shared/types";

/** What a folder holds, counted down through every folder under it. */
export interface KbTotals {
  files: number;
  bytes: number;
  tokens: number;
}

export interface KbTree {
  /** Children of a folder (`null` = the root): folders first, then files, each by name. */
  childrenOf: (parentId: string | null) => KbNode[];
  /** Files anywhere under a folder, with their bytes and tokens summed. */
  totals: (folderId: string) => KbTotals;
  /** Root-first chain of folders ending at `id`. Empty for the root or an unknown id. */
  pathTo: (id: string | null) => KbNode[];
  /** `id` and every folder under it, for a move picker that must not offer a folder's own subtree. */
  subtreeIds: (id: string) => Set<string>;
  /** Every node of the base, flat. */
  nodes: KbNode[];
  byId: (id: string | null | undefined) => KbNode | undefined;
}

const byKindThenName = (a: KbNode, b: KbNode) =>
  a.kind === b.kind ? a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }) : a.kind === "folder" ? -1 : 1;

export function buildTree(nodes: KbNode[]): KbTree {
  const index = new Map(nodes.map((n) => [n.id, n]));
  const children = new Map<string | null, KbNode[]>();
  for (const n of nodes) {
    const key = n.parent_id ?? null;
    children.set(key, [...(children.get(key) ?? []), n]);
  }
  for (const list of children.values()) list.sort(byKindThenName);

  const childrenOf = (parentId: string | null) => children.get(parentId) ?? [];
  const cache = new Map<string, KbTotals>();
  const totals = (folderId: string): KbTotals => {
    const cached = cache.get(folderId);
    if (cached) return cached;
    const sum = { files: 0, bytes: 0, tokens: 0 };
    for (const child of childrenOf(folderId)) {
      const part = child.file ? { files: 1, bytes: child.file.bytes, tokens: child.file.tokens ?? 0 } : totals(child.id);
      sum.files += part.files;
      sum.bytes += part.bytes;
      sum.tokens += part.tokens;
    }
    cache.set(folderId, sum);
    return sum;
  };

  const pathTo = (id: string | null) => {
    const path: KbNode[] = [];
    for (let n = id ? index.get(id) : undefined; n; n = n.parent_id ? index.get(n.parent_id) : undefined) path.unshift(n);
    return path;
  };

  const subtreeIds = (id: string) => {
    const ids = new Set([id]);
    for (const current of ids) childrenOf(current).forEach((c) => ids.add(c.id));
    return ids;
  };

  return { childrenOf, totals, pathTo, subtreeIds, nodes, byId: (id) => (id ? index.get(id) : undefined) };
}

export const useTree = (nodes: KbNode[] | undefined): KbTree => useMemo(() => buildTree(nodes ?? []), [nodes]);
